import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { TrendUp, Brain, Globe, Lightning, ArrowRight, ShieldCheck, ChartLineUp, Users, Wallet, Sparkle, Sun, Moon } from '@phosphor-icons/react';
import axios from 'axios';
import BrandLogo from '@/components/BrandLogo';
import { useTheme } from '@/contexts/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const { user } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [trendingMarkets, setTrendingMarkets] = useState([]);

  useEffect(() => {
    axios.get(`${API}/stats/public`).then(r => setStats(r.data)).catch(() => {});
    axios.get(`${API}/markets/trending`).then(r => setTrendingMarkets(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[var(--oh-bg)] grid-bg" data-testid="landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-[var(--oh-border)] h-16" style={{ backgroundColor: 'var(--oh-glass)' }} data-testid="landing-header">
        <div className="max-w-7xl mx-auto h-full px-4 md:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo variant="header" />
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/leaderboard" className="text-[var(--oh-text-secondary)] hover:text-[var(--oh-text)] text-sm font-medium transition-colors">Leaderboard</Link>
            <a href="#features" className="text-[var(--oh-text-secondary)] hover:text-[var(--oh-text)] text-sm font-medium transition-colors">Features</a>
            <a href="#how-it-works" className="text-[var(--oh-text-secondary)] hover:text-[var(--oh-text)] text-sm font-medium transition-colors">How It Works</a>
          </nav>
          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} data-testid="landing-theme-toggle"
              className="w-9 h-9 rounded-lg flex items-center justify-center border border-[var(--oh-border)] hover:border-[var(--oh-border-hover)] transition-colors"
              style={{ backgroundColor: 'var(--oh-bg-secondary)' }}>
              {isDark ? <Sun size={16} className="text-[#F59E0B]" weight="fill" /> : <Moon size={16} className="text-[#6366f1]" weight="fill" />}
            </button>
            {user ? (
              <Button onClick={() => navigate('/markets')} data-testid="landing-go-to-markets"
                className="bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold hover:scale-[1.02] transition-transform h-10 px-6">
                Go to Markets <ArrowRight size={16} className="ml-2" />
              </Button>
            ) : (
              <>
                <Button onClick={() => navigate('/auth')} variant="ghost" className="text-[var(--oh-text-secondary)] hover:text-white h-10" data-testid="landing-login-btn">
                  Sign In
                </Button>
                <Button onClick={() => navigate('/auth')} data-testid="landing-signup-btn"
                  className="bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold hover:scale-[1.02] transition-transform h-10 px-6">
                  Get Started <ArrowRight size={16} className="ml-2" />
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 md:px-8 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[rgba(0,255,163,0.03)] rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[rgba(0,184,255,0.03)] rounded-full blur-[120px]" />
        </div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="flex items-center justify-center gap-2 mb-6 animate-fade-up">
            <div className="w-2 h-2 rounded-full bg-[#00FFA3] pulse-dot" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#00FFA3]">AI-Powered Prediction Markets</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl text-[var(--oh-text)] tracking-tight leading-none mb-6 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            Shape Your<br />
            <span className="text-brand-gradient">Future</span>
          </h1>
          <p className="text-[var(--oh-text-secondary)] text-base md:text-lg max-w-2xl mx-auto mb-10 animate-fade-up" style={{ animationDelay: '0.2s' }}>
            Trade on global events with AI-powered research. Our prediction engine generates the right questions, educates you with deep analysis, and lets you profit from your knowledge.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-up" style={{ animationDelay: '0.3s' }}>
            <Button onClick={() => navigate(user ? '/markets' : '/auth')} data-testid="hero-cta-button"
              className="bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold text-lg px-8 py-6 hover:scale-[1.03] transition-transform shadow-[0_0_30px_rgba(0,255,163,0.2)]">
              Start Trading Now <ArrowRight size={20} className="ml-2" />
            </Button>
            <Button onClick={() => navigate('/leaderboard')} variant="outline" data-testid="hero-leaderboard-btn"
              className="bg-[var(--oh-card)] border-[var(--oh-border)] text-[var(--oh-text)] hover:border-[#00B8FF] px-8 py-6 text-lg">
              View Leaderboard
            </Button>
          </div>
        </div>
      </section>

      {/* Live Stats Bar */}
      {stats && (
        <section className="py-6 border-y border-[var(--oh-border)] bg-[var(--oh-card)]/50 backdrop-blur-sm">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-8 md:gap-16 px-4">
            <div className="text-center animate-fade-up">
              <p className="font-mono text-2xl md:text-3xl font-bold text-[var(--oh-text)]" data-testid="stat-markets">{stats.active_markets}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[var(--oh-text-muted)] mt-1">Active Markets</p>
            </div>
            <div className="text-center animate-fade-up" style={{ animationDelay: '0.05s' }}>
              <p className="font-mono text-2xl md:text-3xl font-bold text-[#00FFA3]" data-testid="stat-volume">${stats.total_volume?.toLocaleString()}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[var(--oh-text-muted)] mt-1">Total Volume</p>
            </div>
            <div className="text-center animate-fade-up" style={{ animationDelay: '0.1s' }}>
              <p className="font-mono text-2xl md:text-3xl font-bold text-[var(--oh-text)]" data-testid="stat-users">{stats.user_count}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[var(--oh-text-muted)] mt-1">Traders</p>
            </div>
            <div className="text-center animate-fade-up" style={{ animationDelay: '0.15s' }}>
              <p className="font-mono text-2xl md:text-3xl font-bold text-[#00B8FF]" data-testid="stat-trades">{stats.trade_count}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[var(--oh-text-muted)] mt-1">Trades Placed</p>
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section id="features" className="py-20 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[var(--oh-text)] tracking-tight mb-4 animate-fade-up">
              Powered by <span className="text-brand-gradient">Dual AI</span>
            </h2>
            <p className="text-[var(--oh-text-secondary)] text-base max-w-xl mx-auto animate-fade-up" style={{ animationDelay: '0.1s' }}>
              Two world-class AI models work together to generate markets, analyze trends, and educate you before every trade.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Brain, color: '#00B8FF', title: 'AI Research Engine',
                desc: 'Claude Sonnet 4.5 analyzes every market with deep research, providing key insights, supporting factors, and suggested strategies before you trade.',
                badge: 'Claude 4.5'
              },
              {
                icon: Sparkle, color: '#00FFA3', title: 'Market Generation',
                desc: 'GPT-5.2 scans trending topics and news per country to automatically generate relevant, tradeable prediction questions.',
                badge: 'GPT-5.2'
              },
              {
                icon: Globe, color: '#F59E0B', title: 'Global Coverage',
                desc: 'Markets across Africa, Asia, Europe, Americas, and Middle East. Filter by country and category to find what matters to you.',
                badge: '40+ Countries'
              }
            ].map((f, i) => (
              <div key={i} className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-8 hover:border-[var(--oh-border-hover)] transition-all duration-200 group animate-fade-up" style={{ animationDelay: `${0.1 * i}s` }}>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: `${f.color}15` }}>
                  <f.icon size={24} style={{ color: f.color }} weight="fill" />
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="font-heading text-lg text-[var(--oh-text)]">{f.title}</h3>
                  <span className="text-xs px-2 py-0.5 rounded-full border" style={{ color: f.color, borderColor: `${f.color}40`, backgroundColor: `${f.color}10` }}>
                    {f.badge}
                  </span>
                </div>
                <p className="text-sm text-[var(--oh-text-secondary)] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 md:px-8 bg-[var(--oh-card)]/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[var(--oh-text)] tracking-tight text-center mb-16 animate-fade-up">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: '01', icon: Users, title: 'Sign Up', desc: 'Create your account in seconds. Get $10,000 in virtual currency to start trading.' },
              { step: '02', icon: Lightning, title: 'Browse Markets', desc: 'Explore AI-generated prediction markets filtered by country and category.' },
              { step: '03', icon: Brain, title: 'Research & Analyze', desc: 'Use our "Before You Trade" AI panel for deep insights and strategy suggestions.' },
              { step: '04', icon: ChartLineUp, title: 'Trade & Profit', desc: 'Take YES or NO positions. Track your portfolio and climb the leaderboard.' },
            ].map((s, i) => (
              <div key={i} className="relative animate-fade-up" style={{ animationDelay: `${0.1 * i}s` }}>
                <div className="text-center">
                  <span className="font-mono text-5xl font-bold text-[#1F2231] block mb-4">{s.step}</span>
                  <div className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center bg-[rgba(0,255,163,0.08)] border border-[rgba(0,255,163,0.15)]">
                    <s.icon size={24} className="text-[#00FFA3]" weight="fill" />
                  </div>
                  <h3 className="font-heading text-base text-[var(--oh-text)] mb-2">{s.title}</h3>
                  <p className="text-sm text-[var(--oh-text-muted)] leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety & Trust */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[rgba(0,255,163,0.02)] to-[rgba(0,184,255,0.02)]" />
            <div className="relative z-10">
              <ShieldCheck size={48} className="mx-auto mb-6 text-[#00FFA3]" weight="fill" />
              <h2 className="font-heading text-2xl sm:text-3xl text-[var(--oh-text)] tracking-tight mb-4">Trade With Confidence</h2>
              <p className="text-[var(--oh-text-secondary)] text-base max-w-lg mx-auto mb-8">
                Every market is AI-vetted. Every resolution is transparent. Our dual-AI research system ensures you're always informed before taking a position.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-[var(--oh-text-muted)]">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#00FFA3]" />
                  AI-Verified Markets
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#00B8FF]" />
                  Transparent Resolution
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#F59E0B]" />
                  Research Before Trading
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Trending Markets */}
      {trendingMarkets.length > 0 && (
        <section className="py-20 px-4 md:px-8" id="live-markets">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[var(--oh-text)] tracking-tight mb-4">
                Live <span className="text-brand-gradient">Markets</span>
              </h2>
              <p className="text-[var(--oh-text-secondary)] text-base max-w-xl mx-auto">
                Real prediction markets trading right now. Sign up to take your position.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {trendingMarkets.map((m, i) => {
                const yp = (m.yes_probability * 100).toFixed(0);
                const np = (m.no_probability * 100).toFixed(0);
                const daysLeft = Math.max(0, Math.ceil((new Date(m.closes_at) - new Date()) / 86400000));
                return (
                  <div key={m.id} className="rounded-xl border p-5 transition-all hover:scale-[1.02] animate-fade-up" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)', animationDelay: `${0.05 * i}s` }}
                    data-testid={`landing-market-${m.id}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[rgba(0,255,163,0.1)] text-[#00FFA3] border border-[rgba(0,255,163,0.2)]">Live</span>
                      <span className="text-xs uppercase tracking-wider" style={{ color: 'var(--oh-text-muted)' }}>{m.category}</span>
                      <span className="ml-auto text-xs" style={{ color: 'var(--oh-text-muted)' }}>{m.country}</span>
                    </div>
                    <h3 className="font-heading text-sm font-medium mb-3 leading-snug line-clamp-2" style={{ color: 'var(--oh-text)' }}>{m.question}</h3>
                    <div className="flex gap-3 mb-3">
                      <div className="flex-1 text-center rounded-lg p-2 bg-[rgba(0,255,163,0.08)] border border-[rgba(0,255,163,0.15)]">
                        <span className="text-xs text-[#00FFA3] block">YES</span>
                        <span className="font-mono text-lg font-bold text-[#00FFA3]">{yp}%</span>
                      </div>
                      <div className="flex-1 text-center rounded-lg p-2 bg-[rgba(0,184,255,0.08)] border border-[rgba(0,184,255,0.15)]">
                        <span className="text-xs text-[#00B8FF] block">NO</span>
                        <span className="font-mono text-lg font-bold text-[#00B8FF]">{np}%</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs mb-3" style={{ color: 'var(--oh-text-muted)' }}>
                      <span>${(m.total_volume || 0).toLocaleString()} vol</span>
                      <span>{daysLeft}d left</span>
                    </div>
                    <Button onClick={() => navigate(user ? `/market/${m.id}` : '/auth')}
                      className="w-full bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold text-sm h-9"
                      data-testid={`landing-trade-${m.id}`}>
                      {user ? 'Trade Now' : 'Sign Up to Trade'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Disclaimer */}
      <section className="py-12 px-4 md:px-8 border-t border-[var(--oh-border)]">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-xl p-6 border" style={{ backgroundColor: 'var(--oh-card)', borderColor: 'var(--oh-border)' }}>
            <h3 className="font-heading text-base mb-3" style={{ color: 'var(--oh-text)' }}>Important Disclaimer</h3>
            <div className="text-xs leading-relaxed space-y-2" style={{ color: 'var(--oh-text-muted)' }}>
              <p><strong style={{ color: 'var(--oh-text-secondary)' }}>Not Financial Advice:</strong> OneHedge is a prediction market platform for educational and entertainment purposes only. Nothing on this platform constitutes financial, investment, legal, or tax advice. AI-generated content including market questions, research insights, and strategy suggestions may contain errors or biases.</p>
              <p><strong style={{ color: 'var(--oh-text-secondary)' }}>Virtual Currency:</strong> All trading on OneHedge currently uses virtual (simulated) currency with no real-world monetary value. Virtual currency cannot be exchanged for real currency, goods, or services.</p>
              <p><strong style={{ color: 'var(--oh-text-secondary)' }}>Risk Warning:</strong> Prediction markets involve risk. Past performance is not indicative of future results. Users should only participate with amounts they can afford to lose. Market probabilities do not represent guaranteed outcomes.</p>
              <p><strong style={{ color: 'var(--oh-text-secondary)' }}>AI Limitations:</strong> AI models (GPT-5.2, Claude Sonnet 4.5) are tools for analysis, not oracles. They can be wrong, biased, or outdated. Always conduct independent research before making trading decisions.</p>
              <p><strong style={{ color: 'var(--oh-text-secondary)' }}>Age Requirement:</strong> You must be at least 18 years of age to use OneHedge. By using this platform, you confirm you meet this requirement.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[var(--oh-text)] tracking-tight mb-6 animate-fade-up">
            Ready to <span className="text-brand-gradient">Shape Your Future?</span>
          </h2>
          <p className="text-[var(--oh-text-secondary)] text-base mb-8 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            Join thousands of traders using AI-powered insights to make smarter predictions on global events.
          </p>
          <Button onClick={() => navigate(user ? '/markets' : '/auth')} data-testid="cta-button"
            className="bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold text-lg px-10 py-6 hover:scale-[1.03] transition-transform shadow-[0_0_30px_rgba(0,255,163,0.2)] animate-fade-up"
            style={{ animationDelay: '0.2s' }}>
            {user ? 'Go to Markets' : 'Create Free Account'} <ArrowRight size={20} className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-[var(--oh-border)]">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <BrandLogo variant="footer" />
          </div>
          <div className="flex items-center gap-6 text-xs" style={{ color: 'var(--oh-text-muted)' }}>
            <Link to="/leaderboard" className="hover:text-[#00FFA3] transition-colors">Leaderboard</Link>
            <Link to="/terms" className="hover:text-[#00FFA3] transition-colors">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-[#00FFA3] transition-colors">Privacy Policy</Link>
            <span>2026 OneHedge. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
